<template>
  <div class="checkout-page">
    <div v-if="toastMessage" class="error-toast">
        <span>{{ toastMessage }}</span>
        <button class="toast-close" @click="toastMessage = ''">&times;</button>
    </div>

    <!-- Loading Overlay -->
    <div v-if="initializingCart || submitted" class="loading-overlay">
      <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 100 100">
        <circle cx="50" cy="50" fill="none" stroke="#e9ff86" stroke-width="10" r="35" stroke-dasharray="165 57">
          <animateTransform attributeName="transform" type="rotate" repeatCount="indefinite" dur="0.5s" values="0 50 50;360 50 50" keyTimes="0;1" />
        </circle>
      </svg>
    </div>

    <!-- Cart Error Overlay -->
    <div v-if="cartError" class="cart-error-overlay">
      <div class="cart-error-box">
        <div class="cart-error-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
        </div>
        <h2 class="cart-error-title">Error Adding to Cart</h2>
        <p class="cart-error-message">{{ message || 'Something went wrong. Please try again.' }}</p>
        <button class="cart-error-button" @click="goToWeightLossPlan">
          Try Again <span class="btn-arrow">→</span>
        </button>
      </div>
    </div>

    <!-- Payment errors now shown via toast notification -->

    <!-- Address Error -->
    <div v-if="addressError" class="error-popup">
      <span>Our shipping partners do not deliver to PO Box addresses. Please enter a street address.</span>
      <button class="error-popup-close" @click="addressError = false">&times;</button>
    </div>

    <!-- Header -->
    <header class="checkout-header">
      <div class="header-content">
        <div class="header-left">
          <button class="header-back-btn" @click="goBackToWizard">
            <span>←</span>
          </button>
          <img
            src="@/assets/images/NewSelf_Logo.webp"
            alt="NewSelf"
            class="logo"
          />
        </div>
        <div class="header-right">
          <a :href="supportPhoneTel" class="phone-info">
            <svg class="phone-icon-mobile" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
            </svg>
            <span class="phone-text">
              <span class="phone-label">Questions? Call us</span>
              <span v-if="supportPhone" class="phone-number">{{ supportPhone }}</span>
            </span>
          </a>
          <div class="secure-badge">
            <svg class="lock-icon" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd"></path>
            </svg>
            <div class="secure-text-group">
              <p class="secure-text">SECURE</p>
              <p class="payment-text">PAYMENT</p>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Progress Steps -->
    <div class="progress-bar">
      <div class="progress-content">
        <div class="step completed">
          <div class="step-circle completed">
            <svg class="check-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <span class="step-label">Choose Plan</span>
        </div>
        <div class="step-line"></div>
        <div class="step active">
          <div class="step-circle active">
            <div class="step-dot"></div>
          </div>
          <span class="step-label">Checkout</span>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <main class="checkout-main">
      <div class="checkout-grid">
        <!-- Left Column - Form -->
        <div class="form-column">
          <form class="checkout-form" @submit.prevent="getStripeToken">
            <!-- Express Checkout (Apple Pay / Google Pay / Klarna) -->
            <section class="form-section">
              <h2 class="section-title">Express Checkout</h2>
              <div class="express-checkout-box" :class="{ 'single-option': !canMakeExpressCheckout }">
                <express-checkout-button :amount="walletSheetAmountInCents" @token="handleExpressPayment" @can-make-payment="canMakeExpressCheckout = $event" />
                <button v-if="paymentInfo.payment_terms !== 1" type="button" class="klarna-btn" :disabled="submitted" @click="payWithKlarna">
                  <span class="klarna-btn-text">Buy now, pay later with</span>
                  <img class="klarna-logo" src="@/assets/images/Klarna.png" alt="Klarna" />
                </button>
              </div>
              <div class="divider">
                <div class="divider-line"></div>
                <span>OR</span>
                <div class="divider-line"></div>
              </div>
            </section>

            <!-- Shipping Information -->
            <section class="form-section">
              <h2 class="section-title">Shipping Information</h2>
              <div class="form-row">
                <div class="form-group">
                  <label>First name</label>
                  <input type="text" v-model="form.firstName" name="given-name" autocomplete="given-name" required />
                </div>
                <div class="form-group">
                  <label>Last name</label>
                  <input type="text" v-model="form.lastName" name="family-name" autocomplete="family-name" required />
                </div>
              </div>
              <div class="form-group" :class="{ 'address-highlight': addressBlink }">
                <label>Address line 1</label>
                <input
                  ref="addressAutocomplete"
                  type="text"
                  v-model="form.address1"
                  name="address-line1"
                  autocomplete="address-line1"
                  placeholder="Start typing your address..."
                  required
                  @input="syncStateFromZip"
                  @focus="startAutofillDetection"
                  @blur="stopAutofillDetection"
                />
              </div>
              <div class="form-group">
                <label>Address line 2</label>
                <input type="text" v-model="form.address2" name="address-line2" autocomplete="address-line2" placeholder="Apt., suite, unit number, etc. (optional)" />
              </div>
              <div class="form-group">
                <label>City</label>
                <input type="text" v-model="form.city" name="address-level2" autocomplete="address-level2" required @input="syncStateFromZip" />
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>State</label>
                  <div class="state-typeahead">
                    <input
                      type="text"
                      class="state-input"
                      v-model="form.state"
                      name="state"
                      autocomplete="address-level1"
                      placeholder="ST"
                      required
                      @input="handleStateInput"
                      @focus="stateDropdownOpen = true"
                      @blur="closeStateDrop"
                    />
                    <div v-if="stateDropdownOpen && filteredStates.length > 0" class="state-drop">
                      <div v-for="s in filteredStates" :key="s" class="state-opt" @mousedown.prevent="selectFormState(s)">{{ s }}</div>
                    </div>
                  </div>
                </div>
                <div class="form-group">
                  <label>ZIP code</label>
                  <input ref="zipCodeInput" type="text" v-model="form.zipCode" name="postal-code" autocomplete="postal-code" required @input="syncStateFromZip" />
                </div>
              </div>
              <div class="form-group">
                <label>Email</label>
                <input type="email" v-model="form.email" name="email" autocomplete="email" placeholder="you@example.com" required />
              </div>
            </section>

            <!-- Payment Method -->
            <section class="form-section">
              <h2 class="section-title">Payment Method</h2>
              <p class="section-subtitle">All transactions are secure and encrypted.</p>
              <div class="payment-form">
                <stripe-payment-element
                  ref="stripePayment"
                  :base-url="baseUrl"
                  :user-id="user?.id"
                  :user-name="`${form.firstName} ${form.lastName}`.trim()"
                  :user-email="form.email"
                  :hide-address-fields="true"
                  :billing-address="stripeBillingAddress"
                  @ready="hasCardInput = $event"
                  @error="handlePaymentError"
                  @token="handlePaymentToken"
                />
              </div>
            </section>

            <!-- Trust Badges -->
            <div class="trust-badges">
              <div class="trust-item">
                <svg class="trust-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span>Provider review required before fulfillment.</span>
              </div>
              <div class="trust-item">
                <svg class="trust-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span>You will be fully refunded if not approved.</span>
              </div>
              <div class="trust-item">
                <svg class="trust-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
                </svg>
                <span>Most orders are processed within 24 hours.</span>
              </div>
            </div>

            <!-- Checkout API Error Banner -->
            <div v-if="checkoutErrorMessage" class="checkout-error-banner">
                <div class="checkout-error-banner-content">
                    <span class="checkout-error-icon">&#9888;</span>
                    <div class="checkout-error-text">
                        <div class="checkout-error-heading">Payment failed</div>
                        <div class="checkout-error-message">{{ checkoutErrorMessage }}</div>
                        <div class="checkout-error-helper">Please check your card details and try again, or use a different payment method.</div>
                    </div>
                </div>
            </div>

            <!-- Submit Button -->
            <button type="submit" class="submit-btn" :disabled="submitted">
              <div class="submit-content">
                <span>{{ submitted ? 'Processing...' : 'Complete Sign Up' }}</span>
              </div>
              <span class="submit-subtitle">Backed by NewSelf Weight Loss Warranty</span>
            </button>

            <!-- Terms -->
            <p class="terms-text">
             See our
              <a href="https://www.newself.com/terms-of-use" target="_blank">Terms of Service</a>. You acknowledge and agree to NewSelf's
              <a href="https://www.newself.com/telehealth-consent" target="_blank">Telehealth Consent</a>,
              <a href="https://www.newself.com/privacy-policy" target="_blank">Privacy Policy</a>, and
              <a href="https://www.newself.com/terms-of-use" target="_blank">Terms of Service</a>, and understand that services are provided via telehealth by licensed providers.
            </p>

            <!-- Guarantee Box -->
            <div class="guarantee-box">
              <div class="guarantee-content">
                <h3>NewSelf Care Guarantee</h3>
                <p>We're confident you'll reach your weight loss goals with NewSelf. If you don't, we'll refund you — no runaround. It's that simple.</p>
              </div>
              <div class="guarantee-badge">
                <img src="@/assets/images/care-guarantee-badge.webp" alt="NewSelf Guarantee" />
              </div>
            </div>
          </form>
        </div>

        <!-- Right Column - Order Summary -->
        <div class="summary-column">
          <div class="summary-sticky">
            <h2 class="summary-title">Order Summary</h2>

            <div class="order-card">
              <!-- FSA/HSA Badge -->
              <div class="fsa-badge">
                <svg class="fsa-icon" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                </svg>
                <span>FSA/HSA eligible for reimbursement</span>
              </div>

              <div class="order-content">
                <!-- Product Info -->
                <div class="product-row">
                  <div class="product-image">
                    <img :src="getImage" loading="lazy" :alt="getcartItems.name" />
                  </div>
                  <div class="product-details">
                    <div class="product-header">
                      <h3 class="product-name">{{ displayProductName }}</h3>
                      <div class="product-price">
                        <p class="price-label">As low as</p>
                        <p class="price-value"><span class="price-amt" :class="{ 'price-loading': !pricesReady }">${{ displayDailyPrice }}</span><span>/day</span></p>
                      </div>
                    </div>
                    <span class="product-tag">{{ getSupplyDuration }}</span>
                  </div>
                </div>

                <!-- Payment Term Selector (Custom Dropdown) -->
                <div class="payment-term-select">
                  <label>Payment Plan:</label>
                  <div class="custom-dropdown" :class="{ 'is-open': planDropdownOpen }">
                    <button type="button" class="dropdown-trigger" @click="planDropdownOpen = !planDropdownOpen">
                      <span class="price-amt" :class="{ 'price-loading': !pricesReady }">{{ getSelectedPlanLabel }}</span>
                      <svg class="dropdown-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </button>
                    <div v-if="planDropdownOpen" class="dropdown-overlay" @click="planDropdownOpen = false"></div>
                    <div v-if="planDropdownOpen" class="dropdown-menu">
                      <button
                        type="button"
                        class="dropdown-option"
                        :class="{ 'is-selected': paymentInfo.payment_terms === 1 }"
                        @click="selectPaymentTerm(1)"
                      >
                        <svg v-if="paymentInfo.payment_terms === 1" class="check-icon" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                        </svg>
                        <span>Monthly - <span class="price-amt" :class="{ 'price-loading': !pricesReady }">${{ getFirstMonthPrice().toFixed(2) }}</span>/mo</span>
                      </button>
                      <button
                        type="button"
                        class="dropdown-option"
                        :class="{ 'is-selected': paymentInfo.payment_terms === 3 }"
                        @click="selectPaymentTerm(3)"
                      >
                        <svg v-if="paymentInfo.payment_terms === 3" class="check-icon" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                        </svg>
                        <span>3 Months - <span class="price-amt" :class="{ 'price-loading': !pricesReady }">${{ getMonthlyPrice(3).toFixed(2) }}</span>/mo</span>
                      </button>
                      <button
                        type="button"
                        class="dropdown-option"
                        :class="{ 'is-selected': paymentInfo.payment_terms === 6 }"
                        @click="selectPaymentTerm(6)"
                      >
                        <svg v-if="paymentInfo.payment_terms === 6" class="check-icon" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                        </svg>
                        <span>6 Months - <span class="price-amt" :class="{ 'price-loading': !pricesReady }">${{ getMonthlyPrice(6).toFixed(2) }}</span>/mo</span>
                      </button>
                      <button
                        type="button"
                        class="dropdown-option"
                        :class="{ 'is-selected': paymentInfo.payment_terms === 12 }"
                        @click="selectPaymentTerm(12)"
                      >
                        <svg v-if="paymentInfo.payment_terms === 12" class="check-icon" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                        </svg>
                        <span>12 Months - <span class="price-amt" :class="{ 'price-loading': !pricesReady }">${{ getMonthlyPrice(12).toFixed(2) }}</span>/mo (Best Value)</span>
                      </button>
                    </div>
                  </div>
                </div>

                <hr class="order-divider" />

                <!-- Benefits List -->
                <div class="benefits-row">
                  <div class="benefits-list">
                    <div class="benefit-item">
                      <svg class="benefit-icon" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                      </svg>
                      <span>Flexible month-to-month</span>
                    </div>
                    <div class="benefit-item">
                      <svg class="benefit-icon" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                      </svg>
                      <span>Clinically proven</span>
                    </div>
                    <div class="benefit-item">
                      <svg class="benefit-icon" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                      </svg>
                      <span>Results or you don't pay</span>
                    </div>
                    <div class="benefit-item">
                      <svg class="benefit-icon" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                      </svg>
                      <span>No long-term commitment</span>
                    </div>
                  </div>
                  <div class="warranty-badge">
                    <img src="@/assets/images/care-guarantee-badge.webp" alt="Care Guarantee" />
                  </div>
                </div>

                <p class="dose-note">Used to a specific dose? No worries — your price stays the same at any prescribed dose.</p>

                <!-- Promo Code -->
                <div class="promo-row">
                  <input type="text" v-model="couponInput" placeholder="Promo code (optional)" class="promo-input" :disabled="couponApplied" />
                  <button type="button" class="promo-btn" @click="handleCouponSubmit" :disabled="!couponInput || couponApplied">
                    {{ couponApplied ? 'Applied' : 'Redeem' }}
                  </button>
                </div>
                <div v-if="couponError" class="coupon-error">Invalid promo code. Please try again.</div>
                <div v-if="couponApplied" class="coupon-success">Promo code applied! -${{ paymentInfo.discount.toFixed(2) }}</div>

                <!-- Price Breakdown -->
                <div class="price-breakdown">
                  <div class="price-row">
                    <div>
                      <span class="price-item">{{ paymentInfo.payment_terms }}-Month Weight Loss Package</span>
                      <p class="price-subtext">One-time payment · Covers {{ paymentInfo.payment_terms }} months</p>
                    </div>
                    <div class="price-values">
                      <span class="current-price"><span class="price-amt" :class="{ 'price-loading': !pricesReady }">${{ getTotalOrderAmount().toFixed(2) }}</span></span>
                    </div>
                  </div>
                  <div v-if="couponApplied && paymentInfo.discount > 0" class="price-row">
                    <span class="price-item">Promo Discount</span>
                    <span class="discount-value">-${{ paymentInfo.discount.toFixed(2) }}</span>
                  </div>
                  <div class="price-row">
                    <span class="price-item">Overnight Shipping</span>
                    <div class="price-values">
                      <span class="original-price">$19.99</span>
                      <span class="free-value">FREE</span>
                    </div>
                  </div>
                  <div class="price-row">
                    <span class="price-item">Protected by Weight Loss Warranty</span>
                    <span class="activated-value">Activated</span>
                  </div>
                </div>

                <!-- Total -->
                <div class="total-row">
                  <div>
                    <span class="total-label">Total Due Today</span>
                    <p v-if="couponApplied && paymentInfo.discount > 0" class="savings-text">You save ${{ paymentInfo.discount.toFixed(2) }}!</p>
                  </div>
                  <div class="total-values">
                    <span v-if="couponApplied && paymentInfo.discount > 0" class="total-original"><span class="price-amt" :class="{ 'price-loading': !pricesReady }">${{ getTotalOrderAmount().toFixed(2) }}</span></span>
                    <span class="total-price"><span class="price-amt" :class="{ 'price-loading': !pricesReady }">${{ displayTotalPrice }}</span></span>
                    <p class="per-day"><span class="price-amt" :class="{ 'price-loading': !pricesReady }">${{ displayDailyPrice }}</span>/day</p>
                  </div>
                </div>

                <!-- Shipping -->
                <div class="shipping-row">
                  <span class="fedex-logo">Fed<span class="ex">Ex</span></span>
                  <span class="delivery-text">Est. Delivery: <strong>Within 48 Hours</strong></span>
                </div>

                <!-- Benefits Card -->
                <div class="benefits-card">
                  <div class="benefits-header">
                    <p>Your all-inclusive plan includes upgraded benefits:</p>
                    <svg class="gift-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path>
                    </svg>
                  </div>
                  <div class="benefits-items">
                    <div class="benefit-row" v-for="(benefit, idx) in planBenefits" :key="idx">
                      <div class="benefit-icon-box">
                        <img :src="benefit.icon" :alt="benefit.title" />
                      </div>
                      <span class="benefit-title">{{ benefit.title }}</span>
                      <div class="benefit-price">
                        <p class="benefit-original" v-if="benefit.originalPrice">{{ benefit.originalPrice }}</p>
                        <p class="benefit-free">{{ benefit.freeText }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Social Proof -->
            <div class="social-proof">
              <p class="members-text">Join <span class="highlight">200,000+</span> weight loss members</p>
              <div class="rating">
                <div class="stars">
                  <svg v-for="n in 5" :key="n" class="star" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                  </svg>
                </div>
                <span class="rating-text">Excellent 4.7</span>
              </div>
            </div>

            <!-- Testimonial Slider -->
            <div class="testimonial-slider">
              <div class="testimonial-card">
                <div class="testimonial-stars">
                  <svg v-for="n in 5" :key="n" class="star" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                  </svg>
                </div>
                <h4 class="testimonial-title">{{ testimonials[currentTestimonial].title }}</h4>
                <p class="testimonial-text">
                  {{ expandedTestimonial ? testimonials[currentTestimonial].fullText : testimonials[currentTestimonial].text }}
                  <a href="#" @click.prevent="expandedTestimonial = !expandedTestimonial">{{ expandedTestimonial ? 'Show Less' : 'Read More' }}</a>
                </p>
                <div class="testimonial-author">
                  <div class="author-avatar">{{ testimonials[currentTestimonial].initial }}</div>
                  <div class="author-info">
                    <p class="author-name">{{ testimonials[currentTestimonial].name }}</p>
                    <p class="author-verified">
                      <svg class="verified-icon" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
                      </svg>
                      Verified Member
                    </p>
                  </div>
                </div>
                <p class="testimonial-date"><span class="date-label">Date of Experience</span> {{ testimonials[currentTestimonial].date }}</p>
              </div>
              <!-- Navigation Arrows -->
              <button class="slider-arrow slider-arrow-left" @click="prevTestimonial" aria-label="Previous testimonial">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>
              <button class="slider-arrow slider-arrow-right" @click="nextTestimonial" aria-label="Next testimonial">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
              <!-- Progress Dots -->
              <div class="slider-dots">
                <button
                  v-for="(testimonial, index) in testimonials"
                  :key="index"
                  :class="['slider-dot', { active: index === currentTestimonial }]"
                  @click="currentTestimonial = index; expandedTestimonial = false"
                  :aria-label="'Go to testimonial ' + (index + 1)"
                ></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Policy Modals -->
    <TermsOfServiceContent :show="showTermsModal" @close="showTermsModal = false" />
    <PrivacyPolicyContent :show="showPrivacyModal" @close="showPrivacyModal = false" />
    <TelehealthConsentContent :show="showTelehealthModal" @close="showTelehealthModal = false" />

    <!-- Priority Physician Review — offered once the order is captured, never before -->
    <fast-track-upsell-modal
      v-if="fastTrackOffer"
      :addon="fastTrackAddon"
      :payment-method="fastTrackOffer.paymentMethod"
      :context="fastTrackOffer"
      @shown="onFastTrackShown"
      @purchased="onFastTrackPurchased"
      @done="onFastTrackDone"
    />
  </div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";
import { mapActions, mapGetters } from 'vuex';
import TermsOfServiceContent from '@/components/Courtney/TermsOfServiceContent.vue';
import PrivacyPolicyContent from '@/components/Courtney/PrivacyPolicyContent.vue';
import TelehealthConsentContent from '@/components/Courtney/TelehealthConsentContent.vue';
import StripePaymentElement from '../common/StripePaymentElement.vue'
import ExpressCheckoutButton from '../common/ExpressCheckoutButton.vue'
import fastTrackUpsell from '@/mixins/fastTrackUpsell.js'
import { stashKlarnaPurchase } from '@/utils/klarnaPurchaseTracking.js'
import { isRestrictedState, RESTRICTED_STATE_MESSAGE } from '@/utils/restrictedStates'

let stripe = null;

export default {
  name: 'StripeCheckoutV5',
  mixins: [fastTrackUpsell],
  fastTrackFunnelStep: 14,
  fastTrackForm: "start",
  components: {
    TermsOfServiceContent,
    PrivacyPolicyContent,
    TelehealthConsentContent,
    StripePaymentElement,
    ExpressCheckoutButton
  },
  props: ['baseUrl', 'stripeKey', 'googleMapsApiKey'],
  data() {
    return {
      // UI State
      initializingCart: true,
      submitted: false,
      correlationId: null,
      paymentError: false,
      errorMessage: '',
      // Policy Modals
      showTermsModal: false,
      showPrivacyModal: false,
      showTelehealthModal: false,
      cartError: false,
      message: '',
      addressError: false,
      checkoutErrorMessage: '',
      toastMessage: '',
      addressBlink: false,
      couponInput: '',
      couponApplied: false,
      couponError: false,
      couponValid: true,
      ip_address: '',
      currentTestimonial: 0,

      hasCardInput: false,
      canMakeExpressCheckout: false,

      planDropdownOpen: false,
      stateDropdownOpen: false,
      autofillInterval: null,
      // LA and MS are deliberately absent — state law prohibits selling GLP-1
      // medication there. See src/utils/restrictedStates.js. Never add them back.
      usStates: [
        'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
        'HI','ID','IL','IN','IA','KS','KY','ME','MD',
        'MA','MI','MN','MO','MT','NE','NV','NH','NJ',
        'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
        'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
        'DC','PR','VI','GU','AS','MP'
      ],
      stateNameToCode: {
        'ALABAMA':'AL','ALASKA':'AK','ARIZONA':'AZ','ARKANSAS':'AR','CALIFORNIA':'CA',
        'COLORADO':'CO','CONNECTICUT':'CT','DELAWARE':'DE','FLORIDA':'FL','GEORGIA':'GA',
        'HAWAII':'HI','IDAHO':'ID','ILLINOIS':'IL','INDIANA':'IN','IOWA':'IA',
        'KANSAS':'KS','KENTUCKY':'KY','LOUISIANA':'LA','MAINE':'ME','MARYLAND':'MD',
        'MASSACHUSETTS':'MA','MICHIGAN':'MI','MINNESOTA':'MN','MISSISSIPPI':'MS','MISSOURI':'MO',
        'MONTANA':'MT','NEBRASKA':'NE','NEVADA':'NV','NEW HAMPSHIRE':'NH','NEW JERSEY':'NJ',
        'NEW MEXICO':'NM','NEW YORK':'NY','NORTH CAROLINA':'NC','NORTH DAKOTA':'ND','OHIO':'OH',
        'OKLAHOMA':'OK','OREGON':'OR','PENNSYLVANIA':'PA','RHODE ISLAND':'RI','SOUTH CAROLINA':'SC',
        'SOUTH DAKOTA':'SD','TENNESSEE':'TN','TEXAS':'TX','UTAH':'UT','VERMONT':'VT',
        'VIRGINIA':'VA','WASHINGTON':'WA','WEST VIRGINIA':'WV','WISCONSIN':'WI','WYOMING':'WY',
        'DISTRICT OF COLUMBIA':'DC','PUERTO RICO':'PR','U.S. VIRGIN ISLANDS':'VI','GUAM':'GU',
        'AMERICAN SAMOA':'AS','NORTHERN MARIANA ISLANDS':'MP'
      },

      // Form data (matching original)
      form: {
        firstName: '',
        lastName: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        email: '',
        country: 'United States',
        billingZip: ''
      },

      // Payment Info
      paymentInfo: {
        payment_terms: 12, // Default to 12 months (Best Value)
        user_id: '',
        cardToken: '',
        cardBrand: '',
        amount: 499,
        discount: 0,
        medication: '',
        couponCode: '',
        drug_mode: '',
        couponId: null,
        paymentMethod: null,
        cartItems: [],
        cardDetails: [],
        planId: '',
        cardBin: '',
        cardLast4: ''
      },

      // Pricing Config
      pricingConfig: JSON.parse(process.env.VUE_APP_PRICING || '{"semaglutide":{"monthly":{"first":194.97,"regular":194.97},"terms":{"3":449.91,"6":767.82,"12":1199.64}},"tirzepatide":{"monthly":{"first":274.97,"regular":274.97},"terms":{"3":644.91,"6":1129.08,"12":2099.64}}}'),
      // Live plans from /api/get-all-plans keyed by payment_terms: { id, price }.
      // Empty until loadPlans resolves; on fetch failure it stays empty so pricing
      // falls back to pricingConfig and planId falls back to getOfferName.
      apiPlans: {},
      // Gates the price display until the live plan fetch settles, so the env
      // fallback price never flashes on screen before the real one loads.
      pricesReady: false,

      // Testimonials
      expandedTestimonial: false,
      testimonials: [
        {
          title: 'NewSelf helped me achieve my goal!',
          text: 'NewSelf helped me achieve my goal weight! The providers I worked with throughout the process were incredibly supportive...',
          fullText: 'NewSelf helped me achieve my goal weight! The providers I worked with throughout the process were incredibly supportive. They answered all my questions promptly and made sure I felt comfortable every step of the way. The medication was effective and I saw results within the first month. I highly recommend NewSelf to anyone looking to start their weight loss journey.',
          name: 'Neal P.',
          initial: 'N',
          date: 'November 18, 2025'
        },
        {
          title: 'Life-changing experience!',
          text: 'I never thought I could lose this much weight. The medication combined with the support from the NewSelf team made all the difference...',
          fullText: 'I never thought I could lose this much weight. The medication combined with the support from the NewSelf team made all the difference. The whole process was straightforward and the results speak for themselves. I feel healthier, more energetic, and confident. Thank you NewSelf for helping me transform my life!',
          name: 'Sarah M.',
          initial: 'S',
          date: 'November 12, 2025'
        },
        {
          title: 'Finally found what works!',
          text: 'After trying so many programs, NewSelf actually delivered results. The process was easy and the care team was always available...',
          fullText: 'After trying so many programs, NewSelf actually delivered results. The process was easy and the care team was always available to help. I appreciated the personalized approach and the fact that real doctors reviewed my case. The medication worked great for me with minimal side effects. This is the real deal.',
          name: 'Mike R.',
          initial: 'M',
          date: 'November 5, 2025'
        },
        {
          title: 'Exceeded my expectations!',
          text: 'The whole experience from start to finish was seamless. I appreciated how responsive the medical team was to all my questions...',
          fullText: 'The whole experience from start to finish was seamless. I appreciated how responsive the medical team was to all my questions. They took the time to explain everything and ensured I was a good candidate for the medication. The results have been amazing and I feel like a new person. Definitely worth every penny!',
          name: 'Jennifer K.',
          initial: 'J',
          date: 'October 28, 2025'
        }
      ],

      // States list
      states: [
        'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
        'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
        'Kansas', 'Kentucky', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
        'Minnesota', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
        'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
        'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
        'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
        'Wisconsin', 'Wyoming'
      ],

      // Plan Benefits
      planBenefits: [
        { title: 'Consult with Board-certified clinician', originalPrice: '$129', freeText: 'FREE', icon: require('@/assets/images/benefit-video-calls.webp') },
        { title: 'Personalized Weight Loss Plan', originalPrice: '$89', freeText: 'FREE', icon: require('@/assets/images/benefit-phone-assistance.webp') },
        { title: '100% U.S. Based Care Agents', originalPrice: '$69', freeText: 'FREE', icon: require('@/assets/images/benefit-us-care-agents.webp') },
        { title: 'Free Delivery Cold Packed and at your door', originalPrice: '$49', freeText: 'FREE', icon: require('@/assets/images/benefit-refills.webp') },
        { title: 'Ongoing Doctor Support', originalPrice: '$29', freeText: 'FREE', icon: require('@/assets/images/icon_doc.webp') },
        { title: 'NewSelf Weight Loss Warranty', originalPrice: null, freeText: 'Activated', icon: require('@/assets/images/care-guarantee-badge.webp') }
      ]
    }
  },

  beforeDestroy() {
    // Clean up autofill detection interval
    if (this.autofillInterval) {
      clearInterval(this.autofillInterval);
    }
    window.removeEventListener('pageshow', this.handlePageShow);
  },

  async mounted() {
    window.addEventListener('pageshow', this.handlePageShow);
    // Read wizard state from sessionStorage (payment terms + user data for pre-fill)
    const savedState = sessionStorage.getItem('glp1WizardState');
    if (savedState) {
      try {
        const wizardData = JSON.parse(savedState);
        if (wizardData.payment_terms) {
          this.paymentInfo.payment_terms = wizardData.payment_terms;
        }
        // Pre-fill form fields from wizard data
        if (wizardData.first_name) {
          this.form.firstName = wizardData.first_name;
        }
        if (wizardData.last_name) {
          this.form.lastName = wizardData.last_name;
        }
        if (wizardData.email) {
          this.form.email = wizardData.email;
        }
        // Convert state abbreviation to full name
        if (wizardData.state) {
          const stateAbbrevMap = {
            'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
            'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
            'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
            'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
            'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
            'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
            'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
            'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
            'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
            'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
          };
          this.form.state = stateAbbrevMap[wizardData.state] || wizardData.state;
        }
      } catch (e) {
        console.error('Failed to parse wizard state:', e);
      }
    }

    await this.initStripe();
    await this.checkAuthAndInitialize();

    // Load live plan prices + plan ids for the cart's product (no-op if empty)
    await this.loadPlans();
    // Reveal prices once the live fetch settled so the env fallback never flashes.
    this.pricesReady = true;

    await this.getIpAddress();
    await this.initGooglePlaces();
  },

  watch: {
    paymentError(val) {
      if (val) {
        clearTimeout(this._errorTimeout);
        this._errorTimeout = setTimeout(() => { this.paymentError = false; }, 10000);
      }
      if (val && this.errorMessage) {
        this.showToast(this.errorMessage);
      }
    },
    checkoutErrorMessage(val) {
      if (val) {
        this.showToast(val);
      }
    },
    'paymentInfo.payment_terms'() {
      this.couponInput = '';
      this.paymentInfo.couponId = null;
      this.couponApplied = false;
      this.paymentInfo.discount = 0;
    },
    // NOTE: Browser autofill dumps full state names ("MASSACHUSETTS") into the field;
    // Stripe AVS requires 2-letter ISO codes. Normalize anything longer than 2 chars.
    'form.state'(newVal) {
      if (!newVal) return;
      const v = String(newVal).trim();
      if (v.length <= 2) return;
      const code = this.stateNameToCode[v.toUpperCase()];
      if (code) this.form.state = code;
    },
    // Auto-fill billing ZIP from shipping ZIP and infer state from ZIP
    'form.zipCode'(newVal) {
      if (newVal && !this.form.billingZip) {
        this.form.billingZip = newVal;
      }
      // Infer state from ZIP code (handles browser autofill case)
      // Use $nextTick to ensure all autofill changes are processed
      this.$nextTick(() => {
        if (newVal && newVal.length >= 5) {
          const inferredState = this.getStateFromZip(newVal);
          if (inferredState) {
            this.form.state = inferredState;
          }
        }
      });
    },
    // Backup: when city changes, also try to infer state from ZIP
    'form.city'(newVal) {
      if (newVal && this.form.zipCode && this.form.zipCode.length >= 5) {
        const inferredState = this.getStateFromZip(this.form.zipCode);
        if (inferredState) {
          this.form.state = inferredState;
        }
      }
    }
  },

  methods: {
    ...mapActions(['setCartVisibility', 'clearCart', 'removeFromCart']),

    async handlePageShow(event) {
      if (event.persisted) {
        this.submitted = false;
        await this.initStripe();
        if (this.$refs.stripePayment) {
          this.$refs.stripePayment.resetElement()
        }
      }
    },

    // Go back to wizard Step 9 (plan selection)
    goBackToWizard() {
      this.$router.push({ name: 'glp1V5Wizard' })
    },

    // Initialize Stripe
    async initStripe() {
      try {
        stripe = await loadStripe(process.env.VUE_APP_STRIPE_KEY)
      } catch (error) {
        console.error('[Stripe] Failed to initialize:', error)
      }
    },

    handlePaymentError(message) {
      this.paymentError = true
      this.errorMessage = message || 'Invalid card details. Please check your card information.'
      this.submitted = false
    },

    handlePaymentToken(pmToken) {
      this.paymentInfo.cardToken = pmToken
      this.paymentInfo.paymentMethod = 'card'
      this.processPayment()
    },

    async handleExpressPayment(ev) {
      if (this.submitted) { ev.complete('fail'); return; } // a charge is already in flight
      try {
        this.submitted = true
        this.correlationId = crypto.randomUUID()
        this.paymentError = false
        this.paymentInfo.cardToken = ev.tokenId
        this.paymentInfo.cardDetails = {
          number: `XXXXXXXXXXXX${ev.card.last4}`,
          expiration_month: ev.card.exp_month,
          expiration_year: ev.card.exp_year,
          cardBin: '',
          cardBrand: ev.card.brand,
          cardLast4: ev.card.last4,
        }
        this.paymentInfo.cardLast4 = ev.card.last4
        this.paymentInfo.cardBrand = ev.card.brand
        this.paymentInfo.paymentMethod = ev.methodName === 'apple-pay' ? 'apple_pay' : 'google_pay'
        if (ev.payerName) {
          const nameParts = ev.payerName.split(' ')
          this.form.firstName = nameParts[0] || ''
          this.form.lastName = nameParts.slice(1).join(' ') || ''
        }
        if (ev.payerEmail) {
          this.form.email = ev.payerEmail
        }
        await this.processPayment()
        ev.complete('success')
      } catch (error) {
        console.error('[Checkout] Express payment error:', error)
        ev.complete('fail')
        this.paymentError = true
        this.errorMessage = error.response?.data?.message || 'Payment failed. Please try again.'
        this.submitted = false
      }
    },

    // Get IP Address
    async getIpAddress() {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        this.ip_address = data.ip;
      } catch (error) {
        console.error('Error getting IP:', error);
      }
    },

    // Initialize Google Places Autocomplete
    async initGooglePlaces() {
      if (!this.googleMapsApiKey) {
        console.warn('Google Maps API key not provided');
        return;
      }

      // Load Google Maps script if not already loaded
      if (!window.google || !window.google.maps) {
        await this.loadGoogleMapsScript();
      }

      // Initialize autocomplete on the address input
      this.$nextTick(() => {
        const input = this.$refs.addressAutocomplete;
        if (input && window.google && window.google.maps) {
          const autocomplete = new window.google.maps.places.Autocomplete(input, {
            types: ['address'],
            componentRestrictions: { country: 'us' }
          });

          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (place.address_components) {
              this.handlePlaceSelect(place);
            }
          });
        }
      });
    },

    loadGoogleMapsScript() {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.googleMapsApiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    handlePlaceSelect(place) {
      let street = '';
      let city = '';
      let state = '';
      let zip = '';

      for (const component of place.address_components) {
        const types = component.types;

        if (types.includes('street_number')) {
          street = component.long_name + ' ';
        }
        if (types.includes('route')) {
          street += component.long_name;
        }
        if (types.includes('locality')) {
          city = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          // Convert state abbreviation to full name
          const stateAbbrevMap = {
            'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
            'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
            'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
            'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
            'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
            'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
            'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
            'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
            'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
            'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
          };
          state = stateAbbrevMap[component.short_name] || component.long_name;
        }
        if (types.includes('postal_code')) {
          zip = component.long_name;
        }
      }

      // Check for PO Box
      if (this.isPOBox(street)) {
        this.addressError = true;
        setTimeout(() => { this.addressError = false; }, 5000);
        return;
      }

      // Fill in the form fields
      this.form.address1 = street.trim();
      this.form.city = city;
      this.form.state = state;
      this.form.zipCode = zip;
    },

    // Sync state from ZIP code (called on input @change for browser autofill)
    syncStateFromZip() {
      this.$nextTick(() => {
        // By the time @change fires, v-model should be synced
        const zip = this.form.zipCode;
        if (zip && zip.length >= 5) {
          const inferredState = this.getStateFromZip(zip);
          if (inferredState) {
            this.form.state = inferredState;
          }
        }
      });
    },

    // Start polling for autofill detection when address field is focused
    startAutofillDetection() {
      if (this.autofillInterval) return;
      this.autofillInterval = setInterval(() => {
        // Read ZIP directly from DOM using ref
        const zipFromDom = this.$refs.zipCodeInput ? this.$refs.zipCodeInput.value : null;
        if (zipFromDom && zipFromDom.length >= 5 && zipFromDom !== this.form.zipCode) {
          this.form.zipCode = zipFromDom;
          const inferredState = this.getStateFromZip(zipFromDom);
          if (inferredState) {
            this.form.state = inferredState;
          }
        }
      }, 100);
    },

    // Stop autofill detection polling
    stopAutofillDetection() {
      if (this.autofillInterval) {
        clearInterval(this.autofillInterval);
        this.autofillInterval = null;
      }
      // One final sync in case autofill just completed
      this.syncStateFromZip();
    },

    // Get state from ZIP code prefix (first 3 digits)
    getStateFromZip(zip) {
      const prefix = parseInt(zip.substring(0, 3), 10);

      // ZIP code prefix ranges by state
      const zipRanges = [
        { min: 100, max: 149, state: 'New York' },
        { min: 150, max: 196, state: 'Pennsylvania' },
        { min: 197, max: 199, state: 'Delaware' },
        { min: 200, max: 205, state: 'Washington' }, // DC
        { min: 206, max: 219, state: 'Maryland' },
        { min: 220, max: 246, state: 'Virginia' },
        { min: 247, max: 268, state: 'West Virginia' },
        { min: 270, max: 289, state: 'North Carolina' },
        { min: 290, max: 299, state: 'South Carolina' },
        { min: 300, max: 319, state: 'Georgia' },
        { min: 320, max: 349, state: 'Florida' },
        { min: 350, max: 369, state: 'Alabama' },
        { min: 370, max: 385, state: 'Tennessee' },
        { min: 386, max: 397, state: 'Mississippi' },
        { min: 400, max: 427, state: 'Kentucky' },
        { min: 430, max: 459, state: 'Ohio' },
        { min: 460, max: 479, state: 'Indiana' },
        { min: 480, max: 499, state: 'Michigan' },
        { min: 500, max: 528, state: 'Iowa' },
        { min: 530, max: 549, state: 'Wisconsin' },
        { min: 550, max: 567, state: 'Minnesota' },
        { min: 570, max: 577, state: 'South Dakota' },
        { min: 580, max: 588, state: 'North Dakota' },
        { min: 590, max: 599, state: 'Montana' },
        { min: 600, max: 629, state: 'Illinois' },
        { min: 630, max: 658, state: 'Missouri' },
        { min: 660, max: 679, state: 'Kansas' },
        { min: 680, max: 693, state: 'Nebraska' },
        { min: 700, max: 714, state: 'Louisiana' },
        { min: 716, max: 729, state: 'Arkansas' },
        { min: 730, max: 749, state: 'Oklahoma' },
        { min: 750, max: 799, state: 'Texas' },
        { min: 800, max: 816, state: 'Colorado' },
        { min: 820, max: 831, state: 'Wyoming' },
        { min: 832, max: 838, state: 'Idaho' },
        { min: 840, max: 847, state: 'Utah' },
        { min: 850, max: 865, state: 'Arizona' },
        { min: 870, max: 884, state: 'New Mexico' },
        { min: 889, max: 898, state: 'Nevada' },
        { min: 900, max: 961, state: 'California' },
        { min: 967, max: 968, state: 'Hawaii' },
        { min: 970, max: 979, state: 'Oregon' },
        { min: 980, max: 994, state: 'Washington' },
        { min: 995, max: 999, state: 'Alaska' },
        { min: 5, max: 9, state: 'New York' }, // NYC specific (005-009)
        { min: 10, max: 14, state: 'Massachusetts' },
        { min: 15, max: 19, state: 'Massachusetts' }, // More MA
        { min: 20, max: 29, state: 'Massachusetts' },
        { min: 30, max: 38, state: 'New Hampshire' },
        { min: 39, max: 49, state: 'Maine' },
        { min: 50, max: 54, state: 'Vermont' },
        { min: 55, max: 59, state: 'Massachusetts' },
        { min: 60, max: 69, state: 'Connecticut' },
        { min: 70, max: 89, state: 'New Jersey' },
      ];

      for (const range of zipRanges) {
        if (prefix >= range.min && prefix <= range.max) {
          return range.state;
        }
      }

      return null;
    },

    // Navigate to weight loss plan page
    goToWeightLossPlan() {
      this.$router.push('/weight-loss-plan');
    },

    // Cart Initialization
    async checkAuthAndInitialize() {
      if (!this.isLoggedin) {
        try {
          const response = await this.$axios.get(`${this.baseUrl}/api/get-cart/${this.$route.params.cart_id}`);
          if (response.data.status === 'success') {
            let cartItem = response.data.cartItem;
            // Handle offers
            if (cartItem.sema_offer) {
              this.$store.dispatch('addSemaOffer', cartItem.sema_offer);
              delete cartItem.sema_offer;
            }
            if (cartItem.tir_offer) {
              this.$store.dispatch('addTirOffer', cartItem.tir_offer);
              delete cartItem.tir_offer;
            }
            this.$store.dispatch('doLogin', response.data.userData);
            this.$store.dispatch('addToCart', cartItem);
            this.setCartVisibility(false);
            window.location.reload();
          } else {
            this.initializingCart = false;
            this.message = response.data.message;
            this.cartError = true;
          }
        } catch (error) {
          this.initializingCart = false;
          this.message = `${error.message}. Please check the checkout link is correct.`;
          this.cartError = true;
        }
      } else {
        // Pre-fill form fields from logged-in user data (e.g., checkout link auto-login)
        this.prefillFromUserData();
        this.initializingCart = false;
      }
    },

    // Pre-fill shipping form from Vuex user/shipping data (only fills empty fields)
    prefillFromUserData() {
      const user = this.user;
      const shipping = this.$store.getters.shipping;

      if (user) {
        if (!this.form.firstName && user.first_name) this.form.firstName = user.first_name;
        if (!this.form.lastName && user.last_name) this.form.lastName = user.last_name;
        if (!this.form.email && user.email) this.form.email = user.email;
      }

      if (shipping) {
        if (!this.form.address1 && shipping.address_line_1) this.form.address1 = shipping.address_line_1;
        if (!this.form.address2 && shipping.address_line_2) this.form.address2 = shipping.address_line_2;
        if (!this.form.city && shipping.city) this.form.city = shipping.city;
        if (!this.form.state && shipping.state) this.form.state = shipping.state;
        if (!this.form.zipCode && shipping.zip) this.form.zipCode = shipping.zip;
      }
    },

    // Pricing Methods
    async loadPlans() {
      try {
        const productId = this.getMainProduct?.id;
        if (!productId) return;
        const response = await this.$axios.get(`${this.baseUrl}/api/get-all-plans`, {
          params: { isPromo: true, productId }
        });
        if (response.data.status === 'success' && Array.isArray(response.data.plans)) {
          const plans = {};
          response.data.plans.forEach((plan) => {
            if (plan && plan.terms != null && plan.price != null) {
              plans[Number(plan.terms)] = { id: plan.id, price: Number(plan.price) };
            }
          });
          this.apiPlans = plans;
        }
      } catch (error) {
        console.error('Error loading plans:', error);
      }
    },
    getProductPricing() {
      const mainProduct = this.getMainProduct;
      const base = mainProduct.name === 'Semaglutide'
        ? this.pricingConfig.semaglutide
        : this.pricingConfig.tirzepatide;
      // Merge live plan prices over the env pricing so missing terms keep the fallback
      const pricing = { monthly: { ...base.monthly }, terms: { ...base.terms } };
      if (this.apiPlans[1]) {
        pricing.monthly = { first: this.apiPlans[1].price, regular: this.apiPlans[1].price };
      }
      [3, 6, 12].forEach((term) => {
        if (this.apiPlans[term]) {
          pricing.terms[term] = this.apiPlans[term].price;
        }
      });
      return pricing;
    },
    getFirstMonthPrice() {
      return this.getProductPricing().monthly.first;
    },
    getRegularMonthlyPrice() {
      return this.getProductPricing().monthly.regular;
    },
    getTotalPrice(term) {
      return parseFloat(this.getProductPricing().terms[term].toFixed(2));
    },
    getMonthlyPrice(term) {
      return parseFloat((this.getTotalPrice(term) / term).toFixed(2));
    },
    getCurrentMainProductPrice() {
      if (this.paymentInfo.payment_terms === 1) {
        return parseFloat(this.getFirstMonthPrice().toFixed(2));
      }
      return parseFloat(this.getTotalPrice(this.paymentInfo.payment_terms).toFixed(2));
    },
    getTotalOrderAmount() {
      let total = this.getCurrentMainProductPrice();
      this.getAddonProducts.forEach(addon => {
        total += parseFloat((addon.price * addon.quantity).toFixed(2));
      });
      return parseFloat(total.toFixed(2));
    },
    getAddonTotalForPeriod() {
      const total = this.getAddonProducts.reduce((sum, addon) => {
        const addonPrice = addon.price * addon.quantity;
        return sum + parseFloat(addonPrice.toFixed(2));
      }, 0);
      return parseFloat(total.toFixed(2));
    },
    getSavingsText(value) {
      const firstMonth = this.getFirstMonthPrice();
      const regularMonthly = this.getRegularMonthlyPrice();
      const monthlyPlanTotal = firstMonth + (regularMonthly * (value - 1));
      const prepaidTotal = this.getTotalPrice(value);
      const savings = monthlyPlanTotal - prepaidTotal;
      return `${parseFloat(savings.toFixed(2))} Total Savings`;
    },
    getDailyPrice() {
      const total = this.getTotalOrderAmount();
      const days = this.paymentInfo.payment_terms * 30;
      return (total / days).toFixed(2);
    },

    // Address Validation
    isPOBox(address) {
      if (!address) return false;
      const normalized = address.toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ');
      const poBoxRegex = /\b(po box|post office box|p o box)\b/i;
      return poBoxRegex.test(normalized);
    },
    validateForm() {
      return this.form.firstName &&
             this.form.lastName &&
             this.form.address1 &&
             this.form.city &&
             this.form.state &&
             this.form.zipCode &&
             this.form.email;
    },
    async updateAddress() {
      try {
        const addressData = {
          id: this.user.id,
          addressLine1: this.form.address1,
          addressLine2: this.form.address2,
          city: this.form.city,
          state: this.form.state,
          zip: this.form.zipCode
        };
        await this.$axios.post(`${this.baseUrl}/api/save-address`, addressData);
      } catch (error) {
        console.error('Error updating address:', error);
      }
    },

    // Coupon Methods
    async handleCouponSubmit() {
      if (!this.couponInput) return;
      this.paymentInfo.couponCode = this.couponInput;
      await this.validateCoupon();
    },
    async validateCoupon() {
      if (this.couponApplied) return;
      this.couponError = false;
      try {
        const response = await this.$axios.post(`${this.baseUrl}/api/validate-coupon`, {
          coupon: this.paymentInfo.couponCode,
          payment_terms: this.paymentInfo.payment_terms
        });
        if (response.data.status === 'success') {
          this.couponValid = true;
          this.couponApplied = true;
          this.paymentInfo.couponId = response.data.coupon_id;
          if (response.data.c_type === 2) {
            const planAmount = this.getTotalOrderAmount();
            this.paymentInfo.discount = parseFloat(((planAmount * response.data.c_val) / 100).toFixed(2));
          } else {
            this.paymentInfo.discount = response.data.c_val;
          }
        } else {
          this.couponError = true;
        }
      } catch (error) {
        this.couponError = true;
      }
    },

    // Payment Methods
    changePaymentTerms(value) {
      this.paymentInfo.payment_terms = parseInt(value);
    },
    selectPaymentTerm(term) {
      this.paymentInfo.payment_terms = term;
      this.planDropdownOpen = false;
    },
    selectFormState(state) {
      this.form.state = state;
      this.stateDropdownOpen = false;
    },
    // GLP-1 medication cannot be sold into Louisiana or Mississippi — state
    // law. The suggestion list already hides them, but the field is free text
    // and browser autofill / ZIP inference can also land there, so this is the
    // gate that actually stops the payment. Returns true when blocked.
    blockRestrictedShippingState() {
      if (!isRestrictedState(this.form.state)) return false;
      this.paymentError = true;
      this.errorMessage = RESTRICTED_STATE_MESSAGE;
      this.blinkAddress();
      this.submitted = false;
      return true;
    },
    handleStateInput() {
      this.stateDropdownOpen = true;
      const raw = this.form.state || '';
      // Let the form.state watcher convert full-name autofill ("CALIFORNIA") to a code.
      // Here we only constrain direct user input: uppercase, letters only, max 2 chars.
      if (raw.length <= 2) {
        const v = raw.toUpperCase().replace(/[^A-Z]/g, '');
        if (v !== raw) this.form.state = v;
      }
    },
    closeStateDrop() {
      setTimeout(() => { this.stateDropdownOpen = false; }, 150);
    },
    async getStripeToken() {
      if (this.submitted) return; // prevent double-charge on rapid re-click / paint race
      this.submitted = true;
      // One UUID per payment attempt - anchors the backend's Stripe idempotency key
      // so a duplicate of this same attempt can't create a second charge.
      this.correlationId = crypto.randomUUID();
      this.paymentError = false;
      this.addressError = false;
      this.checkoutErrorMessage = '';

      // Validate form fields
      if (!this.validateForm()) {
        this.paymentError = true;
        this.errorMessage = 'Please fill in all required fields.';
        this.blinkAddress();
        if (window.innerWidth <= 479) {
          this.$nextTick(() => {
            this.$refs.addressAutocomplete?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }
        this.submitted = false;
        return;
      }

      if (this.blockRestrictedShippingState()) return;

      // Check for PO Box
      if (this.isPOBox(this.form.address1)) {
        this.addressError = true;
        this.submitted = false;
        return;
      }

      // Create PaymentMethod via Stripe Payment Element — emits 'token' with pm_ ID → handlePaymentToken → processPayment
      if (this.$refs.stripePayment) {
        await this.$refs.stripePayment.confirmPayment();
      }
    },

    // Klarna - Buy Now Pay Later
    async payWithKlarna() {
      if (this.submitted) return; // prevent double-charge on rapid re-click / paint race
      console.log('[Klarna] Pay with Klarna initiated');
      this.submitted = true;
      // One UUID per payment attempt - anchors the backend's Stripe idempotency key
      // so a duplicate of this same attempt can't create a second charge.
      this.correlationId = crypto.randomUUID();
      this.paymentError = false;
      this.addressError = false;
      this.checkoutErrorMessage = '';

      // Validate form (shipping address is required for Klarna)
      if (!this.validateForm()) {
        this.paymentError = true;
        this.errorMessage = 'Please fill in all required fields including shipping address.';
        this.blinkAddress();
        if (window.innerWidth <= 479) {
          this.$nextTick(() => {
            this.$refs.addressAutocomplete?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }
        this.submitted = false;
        return;
      }

      if (this.blockRestrictedShippingState()) return;

      if (this.isPOBox(this.form.address1)) {
        this.addressError = true;
        this.submitted = false;
        return;
      }

      try {
        const shippingAddress = {
          addressLine1: this.form.address1,
          addressLine2: this.form.address2,
          city: this.form.city,
          state: this.form.state,
          zip: this.form.zipCode,
        };

        // Coupon comes off once via chargeAmountInCents (amountInCents is gross here)
        const chargeAmount = this.chargeAmountInCents;

        // Step 1: Create PaymentIntent on backend
        console.log('[Klarna] Creating PaymentIntent, amount (cents):', chargeAmount);
        const response = await this.$axios.post(`${this.baseUrl}/api/create-payment-intent`, {
          id: this.user.id,
          amount: chargeAmount,
          payment_method_types: ['klarna'],
          user_id: this.user.id,
          planId: this.selectedPlanId,
          payment_terms: this.paymentInfo.payment_terms,
          couponId: this.paymentInfo.couponId,
          discount: this.paymentInfo.discount,
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          email: this.form.email,
          cartTotal: parseFloat((this.displayTotal).toFixed(2)),
          shippingAddress,
          billingAddress: shippingAddress,
          cartItems: this.cartItems,
          mainProductTotal: this.getCurrentMainProductPrice(),
          addonTotal: this.getAddonTotalForPeriod(),
          totalOrderAmount: this.getTotalOrderAmount(),
          medication: this.paymentInfo.medication,
          drug_mode: this.paymentInfo.drug_mode,
        }, {
          headers: { 'X-Correlation-Id': this.correlationId }
        });

        const { clientSecret } = response.data;
        console.log('[Klarna] Got clientSecret:', !!clientSecret);

        // Step 2: Confirm Klarna payment (redirects user to Klarna)
        const currentUrl = window.location.origin;
        const cartId = this.$route.params.cart_id;
        const returnUrl = `${currentUrl}/start/checkout/${cartId}/klarna-return`;

        // Snapshot the cart BEFORE redirecting to Klarna so KlarnaReturn.vue — a fresh
        // page load after the redirect — can send cartItems on /api/confirm-klarna-payment
        // ([{ id: productObjectId, quantity, price }]). Keyed to this payment intent so a
        // stale snapshot never attaches to another checkout that shares KlarnaReturn.vue.
        // Mirrors checkoutCallCenter.vue.
        try {
          const klarnaMainItem = (this.cartItems || []).find((i) => i && i.is_addon === false) || (this.cartItems || [])[0] || {};
          const snapshotItems = [{
            id: klarnaMainItem.id,
            quantity: 1,
            price: Number((chargeAmount / 100).toFixed(2)),
          }];
          sessionStorage.setItem('pending_klarna_cart', JSON.stringify({
            payment_intent_id: clientSecret.split('_secret')[0],
            items: snapshotItems,
          }));
        } catch (e) { /* sessionStorage unavailable — confirm falls back to paymentIntentId only */ }

        // Same reason the cart is snapshotted: this page never runs again after the
        // redirect, so it can't fire its own `purchase`. KlarnaReturn.vue fires this
        // once the server confirms the payment.
        stashKlarnaPurchase(clientSecret.split('_secret')[0], {
          funnel_step: 13,
          event_value: Number((chargeAmount / 100).toFixed(2)),
          product_id: this.cartItems?.[0]?.id,
          product_name: this.cartItems?.[0]?.name,
          plan_id: this.selectedPlanId,
          quantity: 1,
          coupon: this.paymentInfo.couponCode || undefined,
          form: "start"
        });

        // Klarna leaves the site, so KlarnaReturn.vue makes the offer instead.
        // Hand it the charge context before the redirect takes this page away.
        this.stashFastTrackOffer(clientSecret.split('_secret')[0], {
          userId: this.user.id,
          paymentMethod: 'klarna',
          paymentTerms: this.paymentInfo.payment_terms,
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          email: this.form.email,
          shippingAddress,
          billingAddress: shippingAddress,
          trackingSessionId: window.trackingSessionId || null
        });

        const { error } = await stripe.confirmKlarnaPayment(clientSecret, {
          payment_method: {
            billing_details: {
              name: `${this.form.firstName} ${this.form.lastName}`,
              email: this.form.email,
              address: {
                line1: this.form.address1,
                line2: this.form.address2 || '',
                city: this.form.city,
                state: this.form.state,
                postal_code: this.form.zipCode,
                country: 'US',
              },
            },
          },
          return_url: returnUrl,
        });

        if (error) {
          console.error('[Klarna] confirmKlarnaPayment error:', error);
          this.paymentError = true;
          this.errorMessage = error.message || 'Klarna payment failed. Please try again.';
          this.submitted = false;
        } else {
          console.log('[Klarna] Redirecting to Klarna...');
        }
        // If no error, user has been redirected to Klarna
      } catch (error) {
        console.error('[Klarna] Payment error:', error);
        this.paymentError = true;
        this.errorMessage = error.response?.data?.message || 'Failed to initiate Klarna payment. Please try again.';
        this.submitted = false;
      }
    },

    async processPayment() {
      try {
        this.paymentInfo.user_id = this.user.id;
        this.paymentInfo.id = this.user.id;
        this.paymentInfo.cartItems = this.cartItems;
        this.paymentInfo.planId = this.selectedPlanId;

        // Build address from form
        const shippingAddress = {
          addressLine1: this.form.address1,
          addressLine2: this.form.address2,
          city: this.form.city,
          state: this.form.state,
          zip: this.form.zipCode
        };

        let sessionId = ""
        try {
          sessionId = await window.tracking.assertSession()
        } catch(error) {
          if (window.trackingSessionId) {
            sessionId = window.trackingSessionId
          }
        }

        let paymentData = {
          ...this.paymentInfo,
          amount: this.chargeAmountInCents,
          cartTotal: parseFloat((this.getTotalOrderAmount() - (this.paymentInfo.discount || 0)).toFixed(2)),
          cartTotalNoDiscount: this.getTotalOrderAmount(),
          offer: '',
          billingAddress: shippingAddress,
          shippingAddress: shippingAddress,
          firstName: this.form.firstName,
          lastName: this.form.lastName,
          email: this.form.email,
          mainProductTotal: this.getCurrentMainProductPrice(),
          addonTotal: this.getAddonTotalForPeriod(),
          totalOrderAmount: this.getTotalOrderAmount(),
          user_agent: window.navigator.userAgent || 'unknown',
          ip_address: this.ip_address || 'unknown',
          tracking_session_id: sessionId
        };

        const response = await this.$axios.post(`${this.baseUrl}/api/checkout-stripe`, paymentData, {
          headers: { 'X-Correlation-Id': this.correlationId }
        });
        const amountCharged = paymentData.amount / 100;

        if (response.data.status === 'success') {
          await this.$store.dispatch('setPrescriptionPreauths', response.data.prescriptionPreauths);
          await this.updateAddress();

          // Save purchased products to localStorage BEFORE clearing cart
          const purchasedItems = this.cartItems.map(item => ({
            name: item.name,
            price: amountCharged.toFixed(2)
          }));
          localStorage.setItem('productsPurchased', JSON.stringify(purchasedItems));

          // Held so the events below read the product even after the cart is
          // emptied on the way out.
          const soldProduct = this.cartItems?.[0];

          let transactionIdentifier = response.data.transaction_id ||
            (response.data.prescriptionPreauths?.[0]?.transactionId) ||
            `${response.data.customerId}_${Date.now()}`;

          if (window.tracking) {
            window.tracking.event('purchase', {
              funnel_step: 13,
              event_value: amountCharged,
              currency: 'USD',
              transaction_id: transactionIdentifier,
              order_id: response.data.order_id,
              event_id: crypto.randomUUID(),
              product_id: soldProduct?.id,
              product_name: soldProduct?.name,
              plan_id: this.paymentInfo.planId,
              quantity: 1,
              coupon: this.paymentInfo.couponCode || undefined,
              form: "start"
            })
          }

          // Offer priority review on top of the confirmed order, then leave.
          // With no add-on to offer this hands straight through to pushToRoute.
          this.offerFastTrackThen({
            userId: this.user.id,
            paymentMethod: this.paymentInfo.paymentMethod || 'card',
            paymentTerms: this.paymentInfo.payment_terms,
            firstName: this.form.firstName,
            lastName: this.form.lastName,
            email: this.form.email,
            shippingAddress,
            billingAddress: shippingAddress,
            trackingSessionId: sessionId
          }, () => this.leaveForUpsell(amountCharged, transactionIdentifier));
        } else {
          const msg = response.data.message || '';
          if (msg.toLowerCase().includes('declined')) {
            this.checkoutErrorMessage = `${msg} Your bank may have sent you a text to approve the charge — reply to approve it and try again, or use a different card.`;
          } else {
            this.checkoutErrorMessage = msg || 'Your payment could not be processed. Please try again or use a different payment method.';
          }
          this.submitted = false; // re-enable only on failure; success stays locked through the redirect
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || '';

        if (errorMessage) {
          this.checkoutErrorMessage = errorMessage.toLowerCase().includes('declined')
            ? `${errorMessage} Your bank may have sent you a text to approve the charge — reply to approve it and try again, or use a different card.`
            : errorMessage;
        } else {
          this.checkoutErrorMessage = 'Your payment could not be processed. Please try again or use a different payment method.';
        }
        this.submitted = false;
      }
    },
    // The cart is emptied HERE, on the way out, and never while this page is
    // still on screen.
    //
    // This whole page renders off the cart: getMainProduct is
    // `cartItems.find(...) || cartItems[0]`, which is undefined once the cart is
    // empty, and getImage, getcartItems.name, displayProductName and every price
    // (getProductPricing -> getTotalOrderAmount -> displayTotal) dereference it
    // with no guard. Clearing mid-page therefore throws inside render, and Vue
    // aborts the whole patch — so the priority-review modal that was just
    // switched on never mounts, never emits `shown`, and the mixin's 4s watchdog
    // gives up and redirects without ever asking the patient. That was the
    // "four seconds then straight to the upsell page" bug.
    //
    // The cart still ends up cleared before the route changes, so nothing
    // downstream sees a different end state.
    async leaveForUpsell(amountCharged, transaction_id) {
      try {
        await this.clearCart();
      } catch (error) {
        // The order is already paid. A failed cart clear must never cost the
        // patient their receipt.
        console.error('[checkout] clearCart failed on exit:', error);
      }
      this.pushToRoute(amountCharged, transaction_id);
    },

    pushToRoute(amountCharged, transaction_id) {
      try {
        const query = {};
        if (this.paymentInfo.payment_terms !== null) {
          query.terms = String(this.paymentInfo.payment_terms);
        }
        query.amount = amountCharged;
        query.trans_id = transaction_id;
        query.payment_terms = 1;//'1';
        if (this.paymentInfo.paymentMethod) {
          query.pm = this.paymentInfo.paymentMethod;
        }
        this.$router.push({
          name: 'upSellPage',
          query
        });
      } catch (error) {
        console.log(error);
        this.$router.push({
          name: 'upSellPage',
          query: { payment_terms: '1' }
        });
      }
    },

    showToast(msg) {
      this.toastMessage = '';
      this.$nextTick(() => {
        this.toastMessage = msg;
        setTimeout(() => { this.toastMessage = ''; }, 5000);
      });
    },
    blinkAddress() {
      this.addressBlink = false;
      this.$nextTick(() => {
        this.addressBlink = true;
        setTimeout(() => { this.addressBlink = false; }, 1500);
      });
    },

    // Utility
    nextTestimonial() {
      this.currentTestimonial = (this.currentTestimonial + 1) % this.testimonials.length;
      this.expandedTestimonial = false;
    },
    prevTestimonial() {
      this.currentTestimonial = this.currentTestimonial === 0
        ? this.testimonials.length - 1
        : this.currentTestimonial - 1;
      this.expandedTestimonial = false;
    }
  },

  beforeRouteLeave(to, from, next) {
    // Track checkout abandonment if user leaves without completing
    if (!this.submitted && this.cartTotal > 0) {
      try {
        if (window.tracking) {
          window.tracking.event('checkout_abandoned', {
            funnel_step: 15,
            cart_value: this.cartTotal,
            target_route: to.path,
            form: "start"
          });
        }
      } catch (error) {
        console.log(error);
      }
    }
    next();
  },

  computed: {
    ...mapGetters(['user', 'cartItems', 'cartTotal', 'cartDiscount', 'cartTotalNoDiscount']),

    filteredStates() {
      const q = (this.form.state || '').toUpperCase();
      if (!q) return this.usStates;
      return this.usStates.filter(s => s.startsWith(q));
    },

    // NOTE: Maps the form's address fields to the shape StripePaymentElement expects.
    // Required because hide-address-fields="true" removes Stripe's Country/ZIP UI.
    stripeBillingAddress() {
      return {
        addressLine1: this.form.address1,
        addressLine2: this.form.address2,
        city: this.form.city,
        state: this.form.state,
        zip: this.form.zipCode,
      };
    },

    getMainProduct() {
      console.log(this.cartItems)
      return this.cartItems.find(item => item.is_addon === false) || this.cartItems[0];
    },
    getAddonProducts() {
      return this.cartItems.filter(item => item.is_addon === true);
    },
    getcartItems() {
      return this.getMainProduct;
    },
    getImage() {
      return this.getMainProduct.image;
    },
    getOfferName() {
      let offer = this.$store.getters.offerTir;
      if (this.getMainProduct.name === 'Semaglutide') {
        offer = this.$store.getters.offerSema;
      }
      return offer;
    },
    getSupplyDuration() {
      if (this.paymentInfo.payment_terms === 1 || this.paymentInfo.payment_terms === 3) {
        return '3 Month Supply';
      } else if (this.paymentInfo.payment_terms === 6) {
        return '3 month supply now, 3 more in 90 days.';
      }
      return '3 Vials Every 90 Days for a Year.';
    },
    getSelectedPlanLabel() {
      const term = this.paymentInfo.payment_terms;
      if (term === 1) return `Monthly - $${this.getFirstMonthPrice().toFixed(2)}/mo`;
      if (term === 3) return `3 Months - $${this.getMonthlyPrice(3).toFixed(2)}/mo`;
      if (term === 6) return `6 Months - $${this.getMonthlyPrice(6).toFixed(2)}/mo`;
      if (term === 12) return `12 Months - $${this.getMonthlyPrice(12).toFixed(2)}/mo (Best Value)`;
      return 'Select a plan';
    },
    isLoggedin() {
      return this.$store.getters.isAuthenticated;
    },
    amountInCents() {
      return Math.round(this.getTotalOrderAmount() * 100);
    },
    selectedPlanId() {
      // Plan ObjectId for the selected term (backend charges the plan price off this)
      const plan = this.apiPlans[this.paymentInfo.payment_terms];
      return plan ? plan.id : this.getOfferName;
    },
    chargeAmountInCents() {
      // amountInCents is the GROSS plan+addon total (drives the display line items);
      // the coupon comes off exactly once here so card, Klarna, and express wallet
      // all charge the same single-discounted amount (matches Group A behavior).
      const discountCents = Math.round((this.paymentInfo.discount || 0) * 100);
      return Math.max(0, this.amountInCents - discountCents);
    },
    finalTotal() {
      const total = this.getTotalOrderAmount();
      return this.couponApplied ? total - this.paymentInfo.discount : total;
    },

    // Price display computed properties
    getFirstMonthDollars() {
      return Math.floor(this.getFirstMonthPrice());
    },
    getFirstMonthCents() {
      const price = this.getFirstMonthPrice();
      return Math.round((price - Math.floor(price)) * 100).toString().padStart(2, '0');
    },
    displayProductName() {
      return `${this.getMainProduct.name} ${this.paymentInfo.payment_terms}-Month Plan`;
    },
    // What the Apple/Google Pay sheet shows. Display only: the express element is
    // created with paymentMethodCreation:'manual', so it tokenizes and never charges.
    walletSheetAmountInCents() {
      return this.chargeAmountInCents;
    },
    // What the customer sees as their total.
    displayTotal() {
      return parseFloat(Number(this.finalTotal).toFixed(2));
    },
    displayTotalPrice() {
      return this.displayTotal.toFixed(2);
    },
    displayDailyPrice() {
      return this.getDailyPrice();
    }
  }
}
</script>

<style scoped>
/* Price loading skeleton — hides env fallback price until live promo price loads */
.price-amt {
  display: inline-block;
}
.price-loading {
  color: transparent !important;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  min-width: 3ch;
}
.price-loading::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #e9e9e9 25%, #f5f5f5 50%, #e9e9e9 75%);
  background-size: 200% 100%;
  animation: price-shimmer 1.1s ease-in-out infinite;
}
@keyframes price-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Global box-sizing for proper width calculations */
.checkout-page *,
.checkout-page *::before,
.checkout-page *::after {
  box-sizing: border-box;
}

/* Base Variables */
.checkout-page {
  --ns-lime: #e9ff86;
  --ns-teal: #08466f;
  --ns-soft-blue: #c9e8ff;
  --ns-green: #10b981;
  --ns-green-light: #d1fae5;
  --ns-gray-50: #f9fafb;
  --ns-gray-100: #f3f4f6;
  --ns-gray-200: #e5e7eb;
  --ns-gray-300: #d1d5db;
  --ns-gray-400: #9ca3af;
  --ns-gray-500: #6b7280;
  --ns-gray-600: #4b5563;
  --ns-gray-700: #374151;
  --ns-gray-800: #1f2937;
  --ns-gray-900: #111827;

  min-height: 100vh;
  background-color: var(--ns-gray-50);
  font-family: Satoshi, Arial, sans-serif;
  overflow-x: hidden;
  width: 100%;
}

/* Header */
.checkout-header {
  background: white;
  border-bottom: 1px solid var(--ns-gray-200);
  width: 100%;
}

.header-content {
  max-width: 1280px;
  margin: 0 auto;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-back-btn {
  width: 28px;
  height: 28px;
  border: 1px solid var(--ns-gray-300);
  border-radius: 6px;
  background: white;
  color: var(--ns-gray-400);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
}

.header-back-btn span {
  display: block;
  margin-top: -2px;
  font-weight: 300;
}

.header-back-btn:hover {
  background: var(--ns-gray-50);
  color: var(--ns-gray-600);
}

.logo {
  height: 32px;
  width: auto;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 24px;
}

.phone-info {
  text-align: right;
  line-height: 1.3;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.phone-icon-mobile {
  display: none;
  width: 24px;
  height: 24px;
  color: var(--ns-teal);
}

.phone-text {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.phone-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--ns-gray-500);
  line-height: 16px;
  text-align: right;
  margin: 0 0 2px 0;
}

.phone-number {
  font-size: 14px;
  font-weight: 600;
  color: var(--ns-teal);
  text-decoration: none;
  display: block;
}

.secure-badge {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  background: var(--ns-gray-50);
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid var(--ns-gray-200);
}

.lock-icon {
  width: 20px;
  height: 20px;
  color: var(--ns-teal);
}

.secure-text-group {
  display: flex;
  flex-direction: column;
}

.secure-text {
  font-size: 12px;
  font-weight: 600;
  color: var(--ns-teal);
  margin: 0;
  line-height: 1.2;
}

.payment-text {
  font-size: 12px;
  font-weight: 400;
  color: var(--ns-gray-500);
  margin: 0;
  line-height: 1.2;
}

/* Progress Bar */
.progress-bar {
  background: white;
  border-bottom: 2px solid var(--ns-gray-100);
  padding: 24px 16px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: -20px;
  font-family: Satoshi, Arial, sans-serif;
}

.progress-content {
  display: flex;
  align-items: center;
  gap: 0;
}

.step {
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--ns-gray-300);
  background: white;
  transition: all 0.2s;
}

.step-circle.completed {
  background: var(--ns-teal);
  border-color: var(--ns-teal);
}

.step-circle.active {
  border-color: var(--ns-teal);
  background: var(--ns-teal);
}

.check-icon {
  width: 16px;
  height: 16px;
  color: white;
}

.step-dot {
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
}

.step-label {
  font-size: 15px;
  font-weight: 500;
  color: var(--ns-gray-500);
}

.step.completed .step-label,
.step.active .step-label {
  color: var(--ns-teal);
  font-weight: 600;
}

.step-line {
  width: 80px;
  height: 2px;
  background: var(--ns-gray-300);
  margin: 0 16px;
}

/* Main Content */
.checkout-main {
  max-width: 1255px;
  margin: 0 auto;
  padding: 32px 24px;
}

.checkout-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
}

@media (max-width: 1024px) {
  .checkout-grid {
    grid-template-columns: 1fr;
  }

  .summary-column {
    order: -1;
  }
}

/* Form Styles */
.checkout-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--ns-teal);
  margin: 0;
}

.section-subtitle {
  font-size: 14px;
  color: var(--ns-gray-500);
  margin: -12px 0 0 0;
}

/* Express Checkout */
.express-checkout-box {
  background: white;
  border: 1px solid var(--ns-gray-200);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.payment-request-btn-container {
  min-height: 48px;
}

.divider {
  display: flex;
  align-items: center;
  width: 100%;
  gap: 16px;
  margin: 8px 0 -8px 0;
  border: none;
  background: transparent;
}

.divider-line {
  flex: 1;
  height: 1px;
  min-width: 0;
  background: var(--ns-gray-300);
}

.divider span {
  font-size: 14px;
  color: var(--ns-gray-500);
  font-weight: 500;
}

/* Form Inputs */
.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: relative;
}

.form-group.dropdown-open,
.form-row.dropdown-open {
  z-index: 9999;
}

.form-group label {
  font-size: 13px;
  color: var(--ns-gray-600);
  font-weight: 400;
}

.form-group input,
.form-group select {
  padding: 14px 14px;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  font-size: 15px;
  outline: none;
  transition: all 0.2s;
}

.form-group input:focus,
.form-group select:focus {
  border-color: #08466f;
  box-shadow: 0 0 0 3px rgba(8, 70, 111, 0.15);
}

.form-group input::placeholder {
  color: var(--ns-gray-400);
}

.select-wrapper {
  position: relative;
}

.select-wrapper select {
  width: 100%;
  appearance: none;
  background: white;
  padding-right: 36px;
}

.select-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: var(--ns-gray-400);
  pointer-events: none;
}

.select-disabled select {
  background-color: var(--ns-gray-100);
  color: var(--ns-gray-500);
  cursor: not-allowed;
}

.select-disabled .select-arrow {
  opacity: 0.5;
}

/* Card Input */
.card-input-wrapper {
  position: relative;
}

.card-input-wrapper input {
  width: 100%;
  padding-right: 140px;
}

.card-icons {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  gap: 6px;
  align-items: center;
}

.card-brand-icon {
  width: 32px;
  height: 22px;
  border-radius: 3px;
}

/* Card icons below input - smaller, right-aligned, no layout impact */
.form-group:has(#card-number-element) {
  position: relative;
}

.card-icons-below {
  position: absolute;
  right: 0;
  bottom: -10px;
  display: flex;
  gap: 4px;
}

.card-brand-icon-small {
  width: 20px;
  height: 14px;
  border-radius: 2px;
}

/* CVC Input */
.cvc-input-wrapper {
  position: relative;
}

.cvc-input-wrapper input {
  width: 100%;
  padding-right: 50px;
}

.cvc-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  pointer-events: none;
}

/* Payment Form */
.payment-form {
  background: white;
  border: 1px solid var(--ns-gray-200);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.payment-form .form-group {
  gap: 1px;
}

.payment-form .form-group label {
  font-size: 15px;
}

/* Trust Badges */
.trust-badges {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.trust-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.trust-icon {
  width: 16px;
  height: 16px;
  color: var(--ns-green);
  flex-shrink: 0;
}

.trust-item span {
  font-size: 14px;
  color: var(--ns-gray-600);
}

/* Submit Button */
.submit-btn {
  width: 100%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 20px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.submit-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.submit-content {
  font-size: 26px;
  font-weight: 700;
  text-align: center;
}

.submit-subtitle {
  font-size: 14px;
  opacity: 0.9;
  width: 100%;
  text-align: center;
}

/* Klarna Button */
.klarna-btn {
  width: 100%;
  background: #ffb3c7;
  color: #0b0b0b;
  border: none;
  border-radius: 4px;
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
  font-family: inherit;
  height: 48px;
}

.klarna-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 179, 199, 0.5);
}

.klarna-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.klarna-btn-text {
  font-size: 16px;
  font-weight: 600;
}

.klarna-logo {
  height: 18px;
  width: auto;
}

/* Guarantee Box */
.guarantee-box {
  background: white;
  border: 1px solid var(--ns-gray-200);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.guarantee-content h3 {
  font-size: 16px;
  font-weight: 600;
  color: var(--ns-teal);
  margin: 0 0 4px 0;
}

.guarantee-content p {
  font-size: 14px;
  color: var(--ns-gray-600);
  margin: 0;
  line-height: 1.5;
}

.guarantee-badge img {
  width: 77px;
  height: 77px;
  object-fit: contain;
}

/* Terms */
.terms-text {
  font-size: 12px;
  color: var(--ns-gray-500);
  line-height: 1.6;
  margin: 0;
}

.terms-text a {
  color: var(--ns-teal);
  text-decoration: none;
}

.terms-text a:hover {
  text-decoration: underline;
}

/* Summary Column */
.summary-column {
  position: relative;
  min-width: 0; /* Prevent grid blowout from long text */
}

.summary-sticky {
  position: sticky;
  top: 32px;
}

.breadcrumb {
  font-size: 14px;
  margin-bottom: 16px;
}

.breadcrumb-link {
  background: none;
  border: none;
  color: var(--ns-gray-400);
  cursor: pointer;
  padding: 0;
}

.breadcrumb-link:hover {
  color: var(--ns-teal);
}

.breadcrumb-separator {
  margin: 0 8px;
  color: var(--ns-gray-300);
}

.breadcrumb-current {
  color: var(--ns-teal);
  font-weight: 500;
}

.summary-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--ns-teal);
  margin: 0 0 16px 0;
}

/* Order Card */
.order-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--ns-gray-100);
  overflow: hidden;
}

.fsa-badge {
  background: var(--ns-green);
  color: white;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
}

.fsa-icon {
  width: 20px;
  height: 20px;
}

.order-content {
  padding: 1.25rem;
  color: rgb(17, 24, 39);
}

/* Product Row */
.product-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.product-image {
  width: 5rem;
  height: 5rem;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #ffffff 0%, #c9e8ff 100%);
  flex-shrink: 0;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-details {
  flex-grow: 1;
  min-width: 0;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
}

.product-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--ns-teal);
  margin: 0;
  white-space: nowrap;
}

.product-tag {
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  color: rgb(38, 175, 89);
  background-color: rgb(236, 253, 245);
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  white-space: nowrap;
}

.product-price {
  text-align: right;
  flex-shrink: 0;
}

.price-label {
  font-size: 12px;
  color: rgb(107, 114, 128);
  margin: 0;
}

.price-value {
  font-size: 20px;
  font-weight: 700;
  color: var(--ns-teal);
  margin: 0;
}

.price-value span {
  font-size: 14px;
  font-weight: 400;
}

/* Mobile: Stack title and price vertically */
@media (max-width: 480px) {
  .product-header {
    flex-direction: column;
    gap: 4px;
  }

  .product-price {
    text-align: left;
  }

  .price-label,
  .price-value {
    display: inline;
  }

  .price-label {
    margin-right: 4px;
  }

  .price-value {
    font-size: 18px;
  }

  /* Express checkout — side by side on mobile */
  .express-checkout-box {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 16px;
    align-items: stretch;
  }

  .payment-request-btn-container {
    min-height: 48px;
  }

  .payment-request-btn-container:only-child {
    grid-column: 1 / -1;
  }

  .klarna-btn {
    flex-direction: column;
    padding: 4px 6px;
    gap: 2px;
    height: 48px;
    margin-top: 0;
  }

  .klarna-btn-text {
    font-size: 11px;
    white-space: nowrap;
  }

  .klarna-logo {
    height: 16px;
  }

  .klarna-btn:only-child,
  .express-checkout-box.single-option .klarna-btn {
    grid-column: 1 / -1;
  }

  .error-popup {
    min-width: 90%;
  }
}

.order-divider {
  border: none;
  border-top: 1px solid var(--ns-gray-100);
  margin: 16px 0;
}

/* Benefits */
.benefits-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.benefits-list {
  flex-grow: 1;
  font-size: 14px;
  color: rgb(55, 65, 81);
}

.benefit-item {
  display: flex;
  align-items: center;
}

.benefit-item + .benefit-item {
  margin-top: 0.25rem;
}

.benefit-icon {
  width: 1rem;
  height: 1rem;
  color: rgb(38, 175, 89);
  flex-shrink: 0;
}

.warranty-badge {
  flex-shrink: 0;
}

.warranty-badge img {
  width: 72px;
  height: auto;
  object-fit: contain;
}

.dose-note {
  font-size: 12px;
  color: rgb(38, 175, 89);
  margin-top: 1rem;
}

/* Promo */
.promo-row {
  display: flex;
  gap: 8px;
  margin-top: 1rem;
  font-size: 14px;
}

.promo-input {
  flex-grow: 1;
  padding: 0.625rem 1rem;
  border: 2px solid rgb(229, 231, 235);
  border-radius: 8px;
  font-size: 14px;
  outline: none;
}

.promo-input:focus {
  border-color: var(--ns-teal);
  outline: 2px solid transparent;
  outline-offset: 2px;
}

.promo-btn {
  padding: 0.625rem 1.25rem;
  background: var(--ns-lime);
  color: var(--ns-teal);
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
}

.promo-btn:disabled {
  background-color: rgb(209, 213, 219);
  color: rgb(255, 255, 255);
  cursor: not-allowed;
}

/* Price Breakdown */
.price-breakdown {
  border-top: 2px solid rgb(243, 244, 246);
  margin-top: 1rem;
  padding: 1rem 0;
  font-size: 14px;
}

.price-row {
  display: flex;
  justify-content: space-between;
}

.price-row + .price-row {
  margin-top: 0.5rem;
}

.price-item {
  color: rgb(55, 65, 81);
}

.price-subtext {
  font-size: 12px;
  color: rgb(107, 114, 128);
  margin: 0;
}

.price-values {
  text-align: right;
}

.original-price {
  color: rgb(156, 163, 175);
  text-decoration: line-through;
  margin-right: 0.5rem;
}

.current-price {
  font-weight: 500;
}

.discount-value,
.free-value,
.activated-value {
  color: rgb(38, 175, 89);
  font-weight: 500;
}

/* Total Row */
.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-top: 2px solid rgb(229, 231, 235);
}

.total-label {
  font-size: 20px;
  font-weight: 600;
  color: var(--ns-teal);
}

.savings-text {
  font-size: 12px;
  color: rgb(38, 175, 89);
  font-weight: 500;
  margin: 0;
}

.total-values {
  text-align: right;
}

.total-original {
  font-size: 18px;
  color: rgb(156, 163, 175);
  text-decoration: line-through;
  margin-right: 0.5rem;
}

.total-price {
  font-size: 24px;
  font-weight: 700;
  color: var(--ns-teal);
}

.per-day {
  font-size: 12px;
  color: rgb(107, 114, 128);
  margin: 0;
}

/* Shipping Row */
.shipping-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 0.75rem 0;
  border-top: 2px solid rgb(243, 244, 246);
}

.fedex-logo {
  font-size: 20px;
  font-weight: 700;
  color: rgb(126, 34, 206);
}

.fedex-logo .ex {
  color: rgb(249, 115, 22);
}

.delivery-text {
  font-size: 14px;
  color: rgb(75, 85, 99);
}

/* Benefits Card */
.benefits-card {
  background-image: linear-gradient(135deg, #e9ff86 0%, #c9e8ff 100%);
  border: 2px solid #c9e8ff;
  border-radius: 12px;
  padding: 1.25rem;
  margin-top: 1rem;
}

.benefits-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
}

.benefits-header p {
  font-size: 14px;
  font-weight: 600;
  color: var(--ns-teal);
  margin: 0;
}

.gift-icon {
  width: 1.75rem;
  height: 1.75rem;
  color: var(--ns-teal);
}

.benefits-items {
  display: flex;
  flex-direction: column;
}

.benefit-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid rgba(8, 70, 111, 0.15);
}

.benefit-row + .benefit-row {
  margin-top: 1rem;
}

.benefit-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.benefit-icon-box {
  width: 3rem;
  height: 3rem;
  border-radius: 8px;
  border: 2px solid var(--ns-teal);
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.benefit-icon-box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.benefit-title {
  flex-grow: 1;
  font-size: 14px;
  font-weight: 500;
  color: var(--ns-teal);
}

.benefit-price {
  text-align: right;
  font-size: 14px;
}

.benefit-original {
  color: var(--ns-gray-500);
  text-decoration: line-through;
  margin: 0;
}

.benefit-free {
  color: var(--ns-teal);
  font-weight: 600;
  margin: 0;
}

/* Social Proof */
.social-proof {
  text-align: center;
  margin-top: 48px;
  padding: 0;
}

.members-text {
  font-size: 15px;
  font-weight: 600;
  color: var(--ns-teal);
  margin: 0 0 8px 0;
}

.members-text .highlight {
  color: var(--ns-green);
}

.rating {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.stars {
  display: flex;
  gap: 2px;
}

.star {
  width: 20px;
  height: 20px;
  color: var(--ns-green);
}

.rating-text {
  font-size: 14px;
  color: var(--ns-green);
}

/* Testimonial Slider */
.testimonial-slider {
  position: relative;
  margin-top: 48px;
}

.testimonial-card {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 16px;
  padding: 24px;
}

.testimonial-stars {
  display: flex;
  gap: 2px;
  margin-bottom: 12px;
}

.testimonial-stars .star {
  width: 20px;
  height: 20px;
  color: #d4e157;
}

.testimonial-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--ns-teal);
  margin: 0 0 8px 0;
}

.testimonial-text {
  font-size: 14px;
  color: var(--ns-gray-700);
  line-height: 1.6;
  margin: 0 0 12px 0;
}

.testimonial-text a {
  color: #b8a04c;
  text-decoration: none;
  margin-left: 4px;
}

.testimonial-text a:hover {
  text-decoration: underline;
}

.testimonial-author {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}

.author-avatar {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: var(--ns-teal);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
}

.author-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--ns-gray-800);
  margin: 0;
}

.author-verified {
  font-size: 12px;
  color: var(--ns-green);
  margin: 2px 0 0 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

.verified-icon {
  width: 12px;
  height: 12px;
}

.testimonial-date {
  font-size: 12px;
  color: var(--ns-gray-500);
  margin: 12px 0 0 0;
}

.date-label {
  color: var(--ns-green);
  font-weight: 500;
}

/* Slider Navigation Arrows */
.slider-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.slider-arrow svg {
  width: 16px;
  height: 16px;
}

.slider-arrow-left {
  left: -12px;
  background: var(--ns-teal);
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.slider-arrow-left:hover {
  background: #063a5a;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.slider-arrow-right {
  right: -12px;
  top: calc(50% + 0px);
  background: var(--ns-teal);
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.slider-arrow-right:hover {
  background: #063a5a;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Slider Dots */
.slider-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 16px;
}

.slider-dot {
  width: 8px;
  height: 8px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
  background: var(--ns-gray-300);
}

.slider-dot.active {
  width: 16px;
  background: var(--ns-teal);
}

.slider-dot:hover:not(.active) {
  background: var(--ns-gray-400);
}

/* Responsive */
@media (max-width: 640px) {
  .header-content {
    padding: 12px 16px;
  }

  .header-back-btn span {
    font-weight: 250;
  }

  .header-right {
    gap: 12px;
  }

  .phone-text {
    display: none;
  }

  .phone-icon-mobile {
    display: block;
  }

  .checkout-main {
    padding: 16px;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .step-label {
    font-size: 12px;
  }

  .step-circle {
    width: 24px;
    height: 24px;
  }

  .step-dot {
    width: 8px;
    height: 8px;
  }

  .check-icon {
    width: 12px;
    height: 12px;
  }

  /* Testimonial slider on mobile - arrows outside card */
  .testimonial-slider {
    overflow: visible;
    margin: 48px 20px 0 20px;
  }

  .slider-arrow-left {
    left: -16px;
  }

  .slider-arrow-right {
    right: -16px;
  }

  /* Summary column - allow arrows to show */
  .summary-column {
    max-width: 100%;
    overflow: visible;
  }

  .summary-sticky {
    overflow: visible;
  }
}

/* Loading Overlay */
.loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Cart Error Overlay */
.cart-error-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.cart-error-box {
  background: #fff;
  border-radius: 16px;
  padding: 40px;
  max-width: 400px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.cart-error-icon {
  margin-bottom: 20px;
}

.cart-error-title {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
}

.cart-error-message {
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 28px 0;
  line-height: 1.5;
}

.cart-error-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 16px 24px;
  background: #e9ff86;
  border: 1px solid #000;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  color: #000;
  cursor: pointer;
  transition: background 0.2s;
}

.cart-error-button:hover {
  background: #dcf570;
}

.cart-error-button .btn-arrow {
  font-size: 18px;
  font-weight: 900;
}

/* Error Popup */
.error-popup {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: #fee2e2;
  border: 1px solid #ef4444;
  color: #991b1b;
  padding: 12px 24px;
  border-radius: 8px;
  z-index: 2147483647;
  max-width: 90%;
  text-align: center;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.error-popup-close {
  background: none;
  border: none;
  color: #991b1b;
  font-size: 20px;
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
  flex-shrink: 0;
}

/* Stripe Card Element */
.ns-card-element {
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  padding: 14px;
  font-size: 15px;
  background: white;
  min-height: 48px;
}

/* Address Preview */
.address-preview {
  background: var(--ns-gray-50);
  border: 1px solid var(--ns-gray-200);
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 8px;
}

.address-preview p {
  margin: 0;
  font-size: 14px;
  color: var(--ns-gray-700);
  line-height: 1.4;
}

/* Checkbox Wrapper */
.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: var(--ns-gray-600);
}

.checkbox-wrapper input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

/* Payment Term Select - Custom Dropdown */
.payment-term-select {
  margin-top: 16px;
  margin-bottom: 8px;
}

.payment-term-select label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--ns-teal);
  margin-bottom: 6px;
}

.custom-dropdown {
  position: relative;
  z-index: 1;
}

.custom-dropdown.is-open {
  z-index: 9999;
}

.dropdown-trigger {
  width: 100%;
  padding: 12px 12px 12px 12px;
  border: 2px solid var(--ns-soft-blue);
  border-radius: 8px;
  font-size: 14px;
  background: white;
  color: var(--ns-teal);
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
}

.dropdown-trigger span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown-trigger:focus {
  border-color: var(--ns-teal);
  outline: none;
}

.custom-dropdown.is-open .dropdown-trigger {
  border-color: var(--ns-teal);
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.dropdown-arrow {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.custom-dropdown.is-open .dropdown-arrow {
  transform: rotate(180deg);
}

.dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9998;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid var(--ns-teal);
  border-top: none;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  z-index: 9999;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.dropdown-menu-scrollable {
  max-height: 250px;
  overflow-y: auto;
}

/* State Dropdown - Isolated Styles */
.state-dropdown {
  position: relative;
  z-index: 1;
}

.state-dropdown.is-open {
  z-index: 9999;
}

.state-trigger {
  width: 100%;
  padding: 14px;
  border: 1px solid #d8dbe0;
  border-radius: 8px;
  font-size: 15px;
  color: var(--ns-gray-900);
  background: white;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.state-trigger:focus {
  border-color: #08466f;
  outline: none;
}

.state-dropdown.is-open .state-trigger {
  border-color: #08466f;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.state-arrow {
  width: 20px;
  height: 20px;
  color: var(--ns-gray-400);
  flex-shrink: 0;
}

.state-dropdown.is-open .state-arrow {
  transform: rotate(180deg);
}

.state-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9998;
}

.state-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #08466f;
  border-top: none;
  border-radius: 0 0 8px 8px;
  z-index: 9999;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.state-option {
  width: 100%;
  padding: 12px 14px;
  border: none;
  background: white;
  font-size: 15px;
  color: var(--ns-gray-900);
  cursor: pointer;
  text-align: left;
}

.state-option:hover {
  background: #f9fafb;
}

.state-option.is-selected {
  background: #c9e8ff;
  font-weight: 500;
}

.dropdown-option {
  width: 100%;
  padding: 14px 16px;
  border: none;
  background: white;
  color: var(--ns-teal);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: background 0.15s ease;
}

.dropdown-option:hover {
  background: var(--ns-gray-50);
}

.dropdown-option.is-selected {
  background: var(--ns-soft-blue);
  color: var(--ns-teal);
}

.dropdown-option:not(:last-child) {
  border-bottom: 1px solid var(--ns-gray-200);
}

.check-icon {
  width: 18px;
  height: 18px;
  color: var(--ns-teal);
  flex-shrink: 0;
}


/* Coupon Messages */
.coupon-error {
  color: #ef4444;
  font-size: 13px;
  margin-top: 8px;
}

.coupon-success {
  color: #10b981;
  font-size: 13px;
  margin-top: 8px;
  font-weight: 500;
}

/* Address Autocomplete Container */
.address-autocomplete {
  width: 100%;
}

/* Step Dot Animation */
@keyframes jellyPop {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.4);
  }
  75% {
    transform: scale(0.9);
  }
  100% {
    transform: scale(1);
  }
}

.step-dot {
  animation: jellyPop 0.4s ease-out;
}

/* Checkout API Error Banner */
.checkout-error-banner {
    background: #fef2f2;
    border: 1px solid #fca5a5;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
}
.checkout-error-banner-content {
    display: flex;
    gap: 12px;
    align-items: flex-start;
}
.checkout-error-icon {
    font-size: 20px;
    line-height: 1;
    flex-shrink: 0;
}
.checkout-error-heading {
    font-weight: 700;
    color: #991b1b;
    margin-bottom: 4px;
}
.checkout-error-message {
    color: #991b1b;
    margin-bottom: 4px;
    font-size: 14px;
}
.checkout-error-helper {
    color: #b91c1c;
    font-size: 13px;
}

/* Error Toast Notification */
.error-toast {
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    width: 90%;
    max-width: 400px;
    background: #dc2626;
    color: #fff;
    padding: 16px 20px;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 600;
    z-index: 99999999;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    animation: toast-in 0.3s ease, toast-out 0.3s ease 4.7s;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
}
.toast-close {
    background: none;
    border: none;
    color: #fff;
    font-size: 22px;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    opacity: 0.8;
    flex-shrink: 0;
}
.toast-close:hover {
    opacity: 1;
}
@keyframes toast-in {
    from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
    to { opacity: 1; transform: translateX(-50%) translateY(0); }
}
@keyframes toast-out {
    from { opacity: 1; transform: translateX(-50%) translateY(0); }
    to { opacity: 0; transform: translateX(-50%) translateY(-20px); }
}

/* Address Blink Highlight */
.address-highlight :deep(input) {
    animation: blink-red 0.5s ease 3;
}
@keyframes blink-red {
    0%, 100% { border-color: inherit; }
    50% { border-color: #dc2626; box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.3); }
}

.state-typeahead {
    position: relative;
    width: 100%;
}
.state-typeahead .state-input {
    width: 100%;
    box-sizing: border-box;
    text-transform: uppercase;
}
.state-drop {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 180px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #d1d5db;
    border-top: none;
    border-radius: 0 0 8px 8px;
    z-index: 100;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
.state-opt {
    padding: 8px 14px;
    font-size: 14px;
    cursor: pointer;
    color: #1a1a1a;
}
.state-opt:hover {
    background: #f3f4f6;
}
</style>

<style scoped>
/* Prevent horizontal overflow on mobile */
html, body {
  max-width: 100vw;
  overflow-x: hidden;
}

/* Checkout page scrollbar - scoped to this component only */
.checkout-page {
  overflow-x: hidden;
}

.checkout-page::-webkit-scrollbar {
  width: 6px;
}

.checkout-page::-webkit-scrollbar-track {
  background: #f3f4f6;
}

.checkout-page::-webkit-scrollbar-thumb {
  background: #c9e8ff;
  border-radius: 3px;
}

.checkout-page::-webkit-scrollbar-thumb:hover {
  background: #a8d4f5;
}

</style>
